import type * as React from 'react';

import * as api from '@gitbook/api-client';
import {
    assertNonLegacySiteType,
    getFeaturesPerSiteType,
    getSiteProductDefinition,
    SITE_PRICING_PRODUCTS,
    type SitePlanTarget,
} from '@gitbook/app-pricing';
import { filterOutNullable } from '@gitbook/util-ts';

import { getMonthlySitePrice } from '../../state';
import {
    PlanCardRadio,
    PlanCardRadioFeaturesList,
    type PlanCardRadioFeaturesListProps,
} from '../OrganizationBilling/PlanCardRadio';

export function SiteTypeCardRadio({
    title,
    description,
    billing,
    siteType,
    featuresList,
    cardHighlight,
    className,
}: {
    siteType: api.SiteType;
    billing: api.OrganizationBilling;
    title: React.ReactNode;
    description: string;
    featuresList?: React.ReactNode;
    cardHighlight?: string;
    className?: string;
}) {
    assertNonLegacySiteType(siteType);
    const monthlyPrice = getMonthlySitePrice({ siteType, billing });

    return (
        <PlanCardRadio
            className={className}
            plan={siteType}
            title={title}
            description={description}
            price={{
                amount: monthlyPrice,
                period: 'month',
            }}
            featuresList={featuresList}
            cardHighlight={cardHighlight}
        />
    );
}

export interface SiteTypeCardFeaturesListProps
    extends Pick<PlanCardRadioFeaturesListProps, 'columns' | 'featureListIcon'> {
    siteType: api.SiteType;
    target: SitePlanTarget;
    relativeToPreviousPlan?: boolean;
}

export function SiteTypeCardFeaturesList(props: SiteTypeCardFeaturesListProps) {
    const { siteType, target, relativeToPreviousPlan = true, columns = 2, featureListIcon } = props;

    const previousPlanDetails = relativeToPreviousPlan
        ? getPreviousSitePlanDetails(siteType, target)
        : null;
    const previousPlanTitle = previousPlanDetails
        ? getSiteProductDefinition(previousPlanDetails.key).title
        : undefined;
    const features = getFeaturesPerSiteType()[siteType];
    const filteredFeatures = [
        previousPlanDetails && previousPlanTitle && siteType === api.SiteType.Ultimate
            ? {
                  key: `all-${previousPlanDetails.key}-features`,
                  title: `All ${previousPlanTitle} features`,
              }
            : undefined,
        ...features.filter((feature) => {
            // Hide features that are unlisted...
            if ('listedInPlanCards' in feature && feature.listedInPlanCards === false) {
                return false;
            }

            return true;
        }),
    ].filter(filterOutNullable);

    if (filteredFeatures.length === 0) {
        return;
    }

    const filteredFeaturesMap = new Set(filteredFeatures.map((feature) => feature.key));
    const moreFeatures = features.filter((feature) => !filteredFeaturesMap.has(feature.key));

    return (
        <PlanCardRadioFeaturesList
            features={filteredFeatures}
            moreFeatures={siteType !== api.SiteType.Basic ? moreFeatures : undefined}
            columns={columns}
            featureListIcon={featureListIcon}
        />
    );
}

function getPreviousSitePlanDetails(siteType: api.SiteType, target: SitePlanTarget) {
    const targetProducts = SITE_PRICING_PRODUCTS.filter((product) =>
        product.targets.includes(target)
    );
    const curPlanIndex = targetProducts.findIndex((details) => details.key === siteType);
    const previousPlanDetails = curPlanIndex > 0 ? targetProducts[curPlanIndex - 1] : undefined;

    return previousPlanDetails;
}
